import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import FormController from 'common/formController';
import { getDynamicSubscriberList } from './validators';
import {
    createSubscriberList,
    updateSubscriberList
} from 'store/lists/actions';
import { actions } from 'store/schema';
import { updateSchemaFilters } from 'store/schema/actions';

let formController = new FormController();

export default function useSubscriberList(listId, subscriberListData, type) {
    const dispatch = useDispatch();
    const paginatedSchemas = useSelector(store => store.schema.paginatedList);

    const [subscriberList, setSubscriberList] = useState({});
    const [selectedOptionalFields, setSelectedOptionalFields] = useState([]);
    const [selectOpen, setSelectOpen] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});

    const { getPaginatedSchemas } = actions;

    useEffect(() => {
        formController.formStatus = { fields: {} };
        setSubscriberList(subscriberListData || {});
        const optionalFieldsWithValue = (paginatedSchemas
            ?.flatMap(schema => schema?.eventFields || [])
            .filter(event => !event?.mandatory)
            .map(field => ({
                displayName: field.displayName,
                type: field.defaultType,
                name: field.name
            })) || []).filter(field => subscriberListData && subscriberListData[field.name] !== undefined && subscriberListData[field.name] !== '');

        dispatch(updateSchemaFilters({ filterProductNames: ["ListManagement"] }));
        dispatch(getPaginatedSchemas({ force: true }));
        setSelectedOptionalFields(optionalFieldsWithValue);
        setValidationErrors({});
    }, [subscriberListData]);

    const mandatoryTextFields = paginatedSchemas
        ?.flatMap(schema => schema?.eventFields || [])
        .filter(event => event?.mandatory)
        .map(field => ({
            displayName: field.displayName,
            name: field.name
        }));

    const optionalFields = paginatedSchemas
        ?.flatMap(schema => schema?.eventFields || [])
        .filter(event => !event?.mandatory)
        .map(field => ({
            displayName: field.displayName,
            type: field.defaultType,
            name: field.name
        }));

    const availableOptionalFields = optionalFields.filter(field => {
        return !selectedOptionalFields.some(selectedField => selectedField.name === field.name);
    });

    const validate = (saveAttempted = false) => {
        const requiredFields = mandatoryTextFields?.map(field => field.name) || [];
        const fullSubscriberList = {
            ...Object.fromEntries(requiredFields.map(name => [name, subscriberList[name] || ''])),
            ...subscriberList
        };
        const joiValidators = getDynamicSubscriberList(mandatoryTextFields);
        const validation = joiValidators.validate(fullSubscriberList, {
            abortEarly: false,
            allowUnknown: true
        });
        let errors = formController.getFormErrors(validation, { saveAttempted });
        setValidationErrors(errors);
        return errors;
    }

    const onBlur = event => {
        formController.onBlur(event);
        validate();
    }

    const shouldShowError = name => {
        return formController.shouldShowError(validationErrors, name);
    }

    const getErrorMessage = name => {
        return formController.getErrorMessage(validationErrors, name);
    }

    const handleFieldSelect = (field) => {
        setSelectedOptionalFields(prev => [...prev, field]);
    };

    const deleteField = (fieldName) => {
        setSelectedOptionalFields(prev => prev.filter(field => field.name !== fieldName));
        setSubscriberList(prev => {
            const updated = { ...prev };
            delete updated[fieldName];
            return updated;
        });
    };

    const handleChange = (event) => {
        const { name, value } = event.target;
        setSubscriberList(prev => ({
            ...prev,
            [name]: value
        }));
        validate(false);
    };

    const handleDateChange = (date, fieldName) => {
        setSubscriberList(prev => ({
            ...prev,
            [fieldName]: date
        }));
        validate(false);
    };

    const onClickSave = () => {
        let errors = validate(true);
        if (errors.meta.clean) {
            try {
                const processedSubscriberList = { ...subscriberList };
                
                Object.keys(processedSubscriberList).forEach(fieldName => {
                    if (fieldName.toLowerCase().includes('tag') && 
                        typeof processedSubscriberList[fieldName] === 'string') {
                        processedSubscriberList[fieldName] = processedSubscriberList[fieldName]
                            .split(',')
                            .map(tag => tag.trim())
                            .filter(tag => tag !== '');
                    }
                });

                if (type === 'create') {
                    dispatch(createSubscriberList({ listId: listId, subscriberList: processedSubscriberList }));
                } else if (type === 'edit') {
                    dispatch(updateSubscriberList({
                        listId: listId,
                        profileId: subscriberListData.profileId,
                        subscriberList: processedSubscriberList
                    }));
                }
            } catch (error) {
                console.error('Error saving subscriber list:', error);
            }
        }
    }

    const areAllMandatoryFieldsFilled = () => {
        if (!mandatoryTextFields || mandatoryTextFields.length === 0) {
            return false;
        }
        
        const allFieldsFilled = mandatoryTextFields.every(field => {
            const value = subscriberList[field.name];
            return value && value.toString().trim() !== '';
        });
        
        const hasValidationErrors = mandatoryTextFields.some(field => {
            return validationErrors[field.name] && validationErrors[field.name].message;
        });
        
        return allFieldsFilled && !hasValidationErrors;
    };

    return {
        subscriberList,
        selectedOptionalFields,
        validationErrors,
        selectOpen,
        mandatoryTextFields,
        optionalFields,
        availableOptionalFields,
        setSubscriberList,
        setSelectOpen,
        handleFieldSelect,
        deleteField,
        handleChange,
        handleDateChange,
        onClickSave,
        validate,
        onBlur,
        shouldShowError,
        getErrorMessage,
        areAllMandatoryFieldsFilled,
        formController
    };
}